// Videos — route /videos
//
// The render gallery (GET /api/videos): the list of rendered videos. Polls while
// any row is queued|generating, then plays the finished mp4 (<video controls
// src=/media/videos/<id>.mp4>). Each card can be retried (re-render in place) or
// deleted, and surfaces the failure reason on an errored render. Rendering itself
// is triggered from Factory/Stories, not here.

import { Gauge, Loader2, RefreshCw, Trash2, VideoOff, X, Zap } from 'lucide-react'
import { ApiError } from '@/api/client'
import type { Story, Video, VideoStatus } from '@/api/types'
import { useStories } from '@/hooks/useStories'
import { useDeleteVideo, useRetryVideo, useVideos } from '@/hooks/useVideos'
import { formatTime } from '@/lib/utils'
import type { BadgeVariant } from '@/admin-design/_common'
import { Badge, Button, Card, EmptyState } from '@/admin-design/_common'

const STATUS_VARIANT: Record<VideoStatus, BadgeVariant> = {
  queued: 'muted',
  generating: 'info',
  scenes_ready: 'warning',
  ready: 'success',
  error: 'destructive',
}

export default function Videos() {
  const storiesQ = useStories()
  const videosQ = useVideos()

  return (
    <div className="space-y-4">
      <header className="flex items-center gap-3">
        <h1 className="text-xl font-semibold text-foreground">Videos</h1>
        <span className="text-sm text-muted-foreground">{videosQ.data?.length ?? 0}개</span>
      </header>

      {videosQ.isLoading ? (
        <p className="text-sm text-muted-foreground">불러오는 중…</p>
      ) : videosQ.isError ? (
        <p className="text-sm text-destructive">로드 실패: {errorMessage(videosQ.error)}</p>
      ) : !videosQ.data || videosQ.data.length === 0 ? (
        <Card>
          <EmptyState
            title="아직 렌더된 영상이 없어요."
            description="Factory 또는 Stories에서 스토리를 렌더하면 여기에 나타납니다."
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {videosQ.data.map((v) => (
            <VideoCard key={v.id} video={v} storyTitle={storyTitle(storiesQ.data, v.story_id)} />
          ))}
        </div>
      )}
    </div>
  )
}

function VideoCard({ video, storyTitle }: { video: Video; storyTitle: string }) {
  const v = video
  const retryM = useRetryVideo()
  const deleteM = useDeleteVideo()
  // Mid-render rows can't be retried or deleted (the async job still owns them).
  const inProgress = v.status === 'queued' || v.status === 'generating'
  const busy = retryM.isPending || deleteM.isPending

  const onDelete = () => {
    if (!window.confirm('이 영상을 삭제할까요? 되돌릴 수 없습니다.')) return
    deleteM.mutate(v.id)
  }
  const onCancel = () => {
    if (!window.confirm('렌더링을 취소하고 이 영상을 삭제할까요?')) return
    deleteM.mutate(v.id)
  }

  return (
    <Card className="overflow-hidden">
      <div className="flex aspect-video items-center justify-center bg-muted">
        {v.status === 'ready' && v.video_path ? (
          <video src={v.video_path} controls className="h-full w-full bg-black object-contain" />
        ) : v.status === 'error' ? (
          <div className="flex flex-col items-center gap-1 text-destructive">
            <VideoOff size={26} aria-hidden="true" />
            <span className="text-[11px]">렌더 실패</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1 text-muted-foreground">
            <Loader2 size={26} className="animate-spin" aria-hidden="true" />
            <span className="text-[11px]">렌더 중…</span>
          </div>
        )}
      </div>
      <Card.Body className="space-y-2 px-3 py-3">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-xs font-medium text-card-foreground">{storyTitle}</span>
          <Badge shape="square" variant={STATUS_VARIANT[v.status] ?? 'muted'}>
            {v.status}
          </Badge>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1 font-mono text-[10px] text-muted-foreground">
            {v.mode === 'full' ? <Gauge size={11} /> : <Zap size={11} />}
            {v.mode} · story characters · story #{v.story_id}
          </span>
          <span className="font-mono text-[10px] text-muted-foreground">
            {formatTime(v.updated_at)}
          </span>
        </div>

        {v.status === 'error' && (
          <div className="space-y-1 rounded border border-destructive-soft bg-destructive-soft p-2 text-[11px] text-destructive-soft-foreground">
            <p>렌더에 실패했어요. 재시도하거나 삭제하세요.</p>
            {v.error && <p className="break-words font-mono opacity-80">{v.error}</p>}
          </div>
        )}

        <div className="flex items-center justify-end gap-1.5">
          {inProgress ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              loading={deleteM.isPending}
              onClick={onCancel}
              title="렌더링을 취소하고 삭제"
            >
              <X size={14} /> 취소
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                loading={retryM.isPending}
                disabled={busy}
                onClick={() => retryM.mutate(v.id)}
                title="같은 모드로 다시 렌더"
              >
                <RefreshCw size={14} /> 재시도
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                loading={deleteM.isPending}
                disabled={busy}
                onClick={onDelete}
              >
                <Trash2 size={14} /> 삭제
              </Button>
            </>
          )}
        </div>
        {(retryM.isError || deleteM.isError) && (
          <p className="text-[11px] text-destructive">
            {retryM.isError
              ? `재시도 실패: ${errorMessage(retryM.error)}`
              : `삭제 실패: ${errorMessage(deleteM.error)}`}
          </p>
        )}
      </Card.Body>
    </Card>
  )
}

function storyTitle(stories: Story[] | undefined, storyId: number): string {
  const s = stories?.find((x) => x.id === storyId)
  return s?.shooting_plan?.title || s?.concept || `story #${storyId}`
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'unknown error'
}
